import React, { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Avatar,
    IconButton,
    CircularProgress,
    Alert,
} from '@mui/material';
import { 
    PhotoCamera, 
    Delete,
    Upload
} from '@mui/icons-material';
import { useNotify, useTranslate, useRecordContext } from 'react-admin';
import { API_CONFIG } from '../../config/api';
import { tokenManager } from '../../utils/tokenManager';
import { 
    resizeImageToMultipleSizes, 
    cleanupImageUrls, 
    validateImageFile,
    IMAGE_SIZES 
} from '../../utils/imageResizer';

interface ImageUploadFieldProps {
    source: string;
    value?: string;
    onChange: (value: string | null) => void;
    label?: string;
    required?: boolean;
    helperText?: string;
    siteId?: string;
}

export const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
    source,
    value,
    onChange,
    label,
    required = false,
    helperText,
    siteId: propSiteId
}) => {
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const notify = useNotify();
    const translate = useTranslate();
    const record = useRecordContext();
    
    // Get siteId from props or record (for edit mode)
    const siteId = propSiteId || record?.id || 'temp-' + Date.now();

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Clear previous error
        setUploadError(null);
        setUploadProgress(0);

        // Validate file
        const validation = validateImageFile(file);
        if (!validation.valid) {
            setUploadError(validation.error || translate('resources.sites.errors.invalidImageType'));
            return;
        }

        setUploading(true);
        
        try {
            // Step 1: Resize images on the frontend
            setUploadProgress(10);
            console.log('[ImageUpload] Starting image resize...');
            const resizedImages = await resizeImageToMultipleSizes(file, IMAGE_SIZES);
            console.log(`[ImageUpload] Resized to ${resizedImages.length} sizes`);
            
            setUploadProgress(30);

            // Step 2: Prepare FormData with all image sizes
            const formData = new FormData();
            formData.append('siteId', String(siteId));
            
            // Add each resized image to FormData
            resizedImages.forEach((resized, index) => {
                formData.append(`file_${resized.suffix}`, resized.file);
                console.log(`[ImageUpload] Added ${resized.suffix}: ${resized.file.size} bytes`);
            });
            
            // Add metadata
            formData.append('originalName', file.name);
            formData.append('originalSize', String(file.size));
            formData.append('fileType', file.type);

            setUploadProgress(50);

            // Step 3: Upload all sizes to backend
            const token = await tokenManager.getAccessToken();
            if (!token) {
                setUploadError(translate('ra.auth.auth_check_error'));
                setUploading(false);
                return;
            }
            
            console.log('[ImageUpload] Uploading to backend...');
            const response = await fetch(`${API_CONFIG.baseURL}/api/backend/sites/upload-images`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            const data = await response.json();
            setUploadProgress(90);

            if (!response.ok) {
                throw new Error(data.error || 'Upload failed');
            }

            console.log('[ImageUpload] Upload successful:', data);
            
            // Step 4: Update the form value with the returned URLs
            // Store all sizes if available, otherwise just the single URL
            if (data.urls && typeof data.urls === 'object') {
                // Store as JSON string with all sizes
                onChange(JSON.stringify(data.urls));
            } else {
                // Fallback to single URL
                onChange(data.url);
            }
            
            setUploadProgress(100);
            notify('resources.sites.notifications.imageUploaded', { type: 'success' });
            
            // Cleanup object URLs to prevent memory leaks
            cleanupImageUrls(resizedImages);
        } catch (error: any) {
            console.error('Upload error:', error);
            setUploadError(error.message || translate('resources.sites.errors.uploadFailed'));
            notify(error.message || 'Upload failed', { type: 'error' });
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }

        // Reset the input
        event.target.value = '';
    };

    const handleRemove = () => {
        onChange(null);
        notify('resources.sites.notifications.imageRemoved', { type: 'info' });
    };

    // Parse the image URL - it might be a JSON string with multiple sizes
    const getDisplayUrl = (val: string) => {
        if (!val) return '';
        try {
            const parsed = JSON.parse(val);
            // Use medium size for display in admin, fallback to original
            return parsed.medium || parsed.small || parsed.original || val;
        } catch {
            // Not JSON, return as is
            return val;
        }
    };

    return (
        <Box sx={{ mb: 2 }}>
            {label && (
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                    {label} {required && '*'}
                </Typography>
            )}
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
                {value ? (
                    <>
                        <Avatar
                            src={getDisplayUrl(value)}
                            sx={{ 
                                width: { xs: 80, sm: 100 },
                                height: { xs: 80, sm: 100 },
                                border: '1px solid',
                                borderColor: 'divider'
                            }}
                            variant="rounded"
                        />
                        <Box>
                            <input
                                id={`${source}-upload`}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleFileUpload}
                                disabled={uploading}
                            />
                            <label htmlFor={`${source}-upload`}>
                                <Button
                                    variant="outlined"
                                    component="span"
                                    disabled={uploading}
                                    startIcon={uploading ? <CircularProgress size={20} /> : <PhotoCamera />}
                                    size="small"
                                    sx={{ mb: 1 }}
                                >
                                    {uploading 
                                        ? `${translate('resources.sites.actions.uploading')} ${uploadProgress}%`
                                        : translate('resources.sites.actions.changeImage')
                                    }
                                </Button>
                            </label>
                            <Box>
                                <IconButton
                                    onClick={handleRemove}
                                    disabled={uploading}
                                    size="small"
                                    color="error"
                                >
                                    <Delete />
                                </IconButton>
                            </Box>
                        </Box>
                    </>
                ) : (
                    <>
                        <input
                            id={`${source}-upload`}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleFileUpload}
                            disabled={uploading}
                        />
                        <label htmlFor={`${source}-upload`}>
                            <Button
                                variant="contained"
                                component="span"
                                disabled={uploading}
                                startIcon={uploading ? <CircularProgress size={20} /> : <Upload />}
                                color="primary"
                            >
                                {uploading 
                                    ? `${translate('resources.sites.actions.uploading')} ${uploadProgress}%`
                                    : translate('resources.sites.actions.uploadImage')
                                }
                            </Button>
                        </label>
                    </>
                )}
            </Box>
            
            {uploading && uploadProgress > 0 && (
                <Box sx={{ width: '100%', mt: 2 }}>
                    <CircularProgress variant="determinate" value={uploadProgress} />
                </Box>
            )}
            
            {uploadError && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setUploadError(null)}>
                    {uploadError}
                </Alert>
            )}
            
            {helperText && (
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
                    {helperText}
                </Typography>
            )}
        </Box>
    );
};